import * as React from 'react';
import { replaceSvgColorAreas } from '../../utils/svgColorReplace';
import {
  buildDisplaySvg,
  extractSvgInnerContent,
  getTrimViewBoxFromItem,
  resolveTextDisplayPathData,
} from '../../utils/svgDocument';
import { getTextRenderScale, worldToScreen } from '../../utils/textMetrics';
import type { EditorItem, ImageItem, TextItem } from '../../Reducers/Configurator/types';
import type { VisibleRect } from './useEditorViewport';
import { MIN_WORLD_UNIT_MM } from '../../utils/editorUnits';
import { orderItemsForHit } from './hitOrder';

const DRAG_THRESHOLD_PX = 4;
const MIN_HIT_SIZE_MM = MIN_WORLD_UNIT_MM * 2;

type DragState = {
  startClientX: number;
  startClientY: number;
  originX: number;
  originY: number;
  moved: boolean;
};

type DesignItemLayerProps = {
  items: Record<string, EditorItem>;
};

type DesignItemHitLayerProps = {
  items: Record<string, EditorItem>;
  selectedId: string | null;
  viewRect: VisibleRect;
  pxPerMm: number;
  onSelect: (id: string) => void;
  onMoveItem: (id: string, x: number, y: number) => void;
};

/** Visual stacking: text always above image (print preview), no exceptions —
 *  not even for the selected item. */
const visualRank = (item: EditorItem) => (item.type === 'text' ? 1 : 0);

function useItemInteraction({
  item,
  isSelected,
  pxPerMm,
  onSelect,
  onMoveItem,
}: {
  item: EditorItem;
  isSelected: boolean;
  pxPerMm: number;
  onSelect: () => void;
  onMoveItem: (x: number, y: number) => void;
}) {
  const dragRef = React.useRef<DragState | null>(null);
  const safePxPerMm = pxPerMm > 0 ? pxPerMm : 1;

  const handlePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    if (!isSelected) onSelect();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: item.x,
      originY: item.y,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    const dxPx = e.clientX - drag.startClientX;
    const dyPx = e.clientY - drag.startClientY;

    if (
      !drag.moved &&
      (Math.abs(dxPx) > DRAG_THRESHOLD_PX || Math.abs(dyPx) > DRAG_THRESHOLD_PX)
    ) {
      drag.moved = true;
    }

    if (drag.moved) {
      onMoveItem(
        drag.originX + dxPx / safePxPerMm,
        drag.originY + dyPx / safePxPerMm,
      );
    }
  };

  const finishInteraction = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    e.stopPropagation();
    e.preventDefault();

    if ((e.currentTarget as Element).hasPointerCapture(e.pointerId)) {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    }

    dragRef.current = null;
  };

  const stopBubble = (e: React.SyntheticEvent) => e.stopPropagation();

  const pointerProps = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: finishInteraction,
    onPointerCancel: finishInteraction,
    onClick: stopBubble,
    style: { cursor: 'pointer', touchAction: 'none' as const },
  };

  return { pointerProps };
}

// ---------------------------------------------------------------------------
// Visual components (inert — the layer wraps them in pointer-events: none)
// ---------------------------------------------------------------------------

function TextItemVisual({ item }: { item: TextItem }) {
  if (!item.pathData) return null;

  const renderScale = getTextRenderScale(item.width, item.viewBoxWidth);
  const pathData = resolveTextDisplayPathData(item);

  return (
    <g transform={`translate(${item.x}, ${item.y}) scale(${renderScale})`}>
      <path d={pathData} fill={item.color} />
    </g>
  );
}

function ImageItemVisual({ item }: { item: ImageItem }) {
  const { viewBoxWidth, viewBoxHeight } = item;
  const trimX = item.trimViewBoxX ?? 0;
  const trimY = item.trimViewBoxY ?? 0;

  const innerContent = React.useMemo(() => {
    if (!item.svgSource?.trim()) return '';
    const displaySvg = buildDisplaySvg(item.svgSource, getTrimViewBoxFromItem(item));
    const colored = item.colorAreas?.length
      ? replaceSvgColorAreas(displaySvg, item.colorAreas)
      : displaySvg;
    return extractSvgInnerContent(colored);
  }, [
    item.svgSource,
    item.colorAreas,
    item.trimViewBoxX,
    item.trimViewBoxY,
    item.viewBoxWidth,
    item.viewBoxHeight,
  ]);

  const svgProps = {
    x: item.x,
    y: item.y,
    width: item.width,
    height: item.height,
    viewBox: `${trimX} ${trimY} ${viewBoxWidth} ${viewBoxHeight}`,
    preserveAspectRatio: 'xMidYMid meet' as const,
    overflow: 'visible' as const,
  };

  if (innerContent) {
    return <svg {...svgProps} dangerouslySetInnerHTML={{ __html: innerContent }} />;
  }

  return (
    <svg {...svgProps}>
      <image
        href={item.url}
        x={0}
        y={0}
        width={viewBoxWidth}
        height={viewBoxHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Hit component (transparent rect + pointer events, no visuals).
// data-editor-item is read by shouldDelegateToEditorItem() in SvgDesignCanvas
// so the canvas capture handler skips setPointerCapture(SVG) for item clicks.
// ---------------------------------------------------------------------------

function ItemHit({
  item,
  viewRect,
  isSelected,
  pxPerMm,
  onSelect,
  onMoveItem,
}: {
  item: EditorItem;
  viewRect: VisibleRect;
  isSelected: boolean;
  pxPerMm: number;
  onSelect: () => void;
  onMoveItem: (x: number, y: number) => void;
}) {
  const { pointerProps } = useItemInteraction({ item, isSelected, pxPerMm, onSelect, onMoveItem });

  const widthMm = Math.max(item.width, MIN_HIT_SIZE_MM);
  const heightMm = Math.max(item.height, MIN_HIT_SIZE_MM);
  const topLeft = worldToScreen(item.x, item.y, viewRect, pxPerMm);

  return (
    <rect
      data-editor-item="true"
      data-item-id={item.id}
      x={topLeft.x}
      y={topLeft.y}
      width={widthMm * pxPerMm}
      height={heightMm * pxPerMm}
      fill="transparent"
      pointerEvents="all"
      {...pointerProps}
    />
  );
}

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

/** Print-preview visuals only — lives inside the surface-filter group. */
export default function DesignItemLayer({ items }: DesignItemLayerProps) {
  const visualItems = [...Object.values(items)].sort((a, b) => visualRank(a) - visualRank(b));

  return (
    <g pointerEvents="none">
      {visualItems.map((item) =>
        item.type === 'text' ? (
          <TextItemVisual key={item.id} item={item} />
        ) : (
          <ImageItemVisual key={item.id} item={item} />
        ),
      )}
    </g>
  );
}

/** Screen-space hit targets — sibling of the scaled design group, not filtered. */
export function DesignItemHitLayer({
  items,
  selectedId,
  viewRect,
  pxPerMm,
  onSelect,
  onMoveItem,
}: DesignItemHitLayerProps) {
  if (!(pxPerMm > 0)) return null;

  const hitItems = orderItemsForHit(items, selectedId);

  return (
    <g>
      {hitItems.map((item) => (
        <ItemHit
          key={item.id}
          item={item}
          viewRect={viewRect}
          isSelected={selectedId === item.id}
          pxPerMm={pxPerMm}
          onSelect={() => onSelect(item.id)}
          onMoveItem={(x, y) => onMoveItem(item.id, x, y)}
        />
      ))}
    </g>
  );
}
